import { defineStore } from 'pinia'
import {
  type RentalCase,
  type RentalAudit,
  type RentalCaseCustomData,
  type RentalAuditCustomData,
  type RentalCaseDetailedResponse,
  type RentalCaseDetailItem
} from '~/api/types/rentalCase'
import {
  toBEStatus,
  resolveProcessStatus,
  type CaseProcessStatus
} from '~/utils/processStatus'

const parseCustomData = (customData: unknown): RentalCaseCustomData => {
  if (customData && typeof customData === 'string') {
    try {
      return JSON.parse(customData)
    } catch (e) {
      console.warn('[caseStore] Failed to parse customData:', e)
      return {}
    }
  }
  return (customData as RentalCaseCustomData) || {}
}

const parseCaseDetail = (detail: {
  rentalCase: RentalCase
  customer?: RentalCaseDetailItem['customer']
  damagedCar?: RentalCaseDetailItem['damagedCar']
  rentalCar?: RentalCaseDetailItem['rentalCar']
  contactPersons?: RentalCaseDetailItem['contactPersons']
}): RentalCase => ({
  ...detail.rentalCase,
  customData: parseCustomData(detail.rentalCase.customData),
  customerData: detail.customer,
  damagedCarData: detail.damagedCar,
  rentalCarData: detail.rentalCar,
  contactPersons: detail.contactPersons
})

export const useCaseStore = defineStore({
  id: 'cases',

  state: () => ({
    cases: {} as Record<string, RentalCase>,
    audits: {} as Record<string, RentalAudit[]>,  // Keyed by caseId
    loading: false,
    initialized: false
  }),

  getters: {
    allCases: (state) => Object.values(state.cases),
    casesByRentalCarId: (state) => (carId: string) =>
      Object.values(state.cases).filter(c =>
        c.rentalCar === carId || c.rentalCarData?.id === carId
      ),

    lastDamageCaseForCar: (state) => (carId: string): RentalCase | null => {
      const cases = Object.values(state.cases).filter(c =>
        (c.rentalCar === carId || c.rentalCarData?.id === carId) &&
        (c.customData?.damageDrawingContentId || c.customData?.rentedVehicleDamaged)
      )
      if (cases.length === 0) return null
      cases.sort((a, b) => {
        const dateA = a.startDate ? new Date(a.startDate).getTime() : 0
        const dateB = b.startDate ? new Date(b.startDate).getTime() : 0
        return dateB - dateA
      })
      return cases[0]
    },

    auditsForCase: (state) => (caseId: string) => state.audits[caseId] || []
  },

  actions: {
    // ==================== Fetch Operations ====================

    async fetchCases(api: API) {
      if (!api?.rentalCase) {
        console.warn('[caseStore] api.rentalCase not available')
        return
      }

      this.loading = true

      try {
        const response = await api.rentalCase.getAllRentalCasesDetailed()
        const items = Object.values(response) as RentalCaseDetailItem[]

        const newCases: Record<string, RentalCase> = {}
        for (const item of items) {
          if (!item.rentalCase) continue
          const entry = parseCaseDetail(item)
          newCases[entry.id] = entry
        }

        this.cases = newCases
        this.initialized = true
      } catch (e) {
        console.error('[caseStore] Failed to fetch cases:', e)
      } finally {
        this.loading = false
      }
    },

    async get(api: API, id: string): Promise<RentalCase | null> {
      if (!api?.rentalCase) {
        console.warn('[caseStore] api.rentalCase not available')
        return null
      }

      const rawResponse = await api.rentalCase.getRentalCaseDetailed(id)

      const response = rawResponse as unknown as RentalCaseDetailedResponse
      const rc = (response.rentalCase || rawResponse) as RentalCase

      const caseEntry = parseCaseDetail({
        rentalCase: rc,
        customer: response.customer,
        damagedCar: response.damagedCar,
        rentalCar: response.rentalCar,
        contactPersons: response.contactPersons
      })

      this.cases[caseEntry.id] = caseEntry
      return caseEntry
    },

    getCase(id: string): RentalCase | undefined {
      return this.cases[id]
    },

    // ==================== Case CRUD ====================

    async update(api: API, id: string, updates: Partial<RentalCase>): Promise<RentalCase | null> {
      if (!api?.rentalCase) {
        throw new Error('RentalCase API not available')
      }

      this.loading = true

      try {
        const existing = this.cases[id]
        const merged = {
          ...existing,
          ...updates,
          customData: { ...existing?.customData, ...updates.customData }
        }

        const customerId = merged.customer || merged.customerData?.id || ''
        const damagedCarId = merged.damagedCar || merged.damagedCarData?.id || ''
        const rentalCarId = merged.rentalCar || merged.rentalCarData?.id || ''

        const ps = resolveProcessStatus(merged)
        merged.status = toBEStatus(ps)

        await api.rentalCase.update(
          id,
          merged.startDate,
          merged.endDate,
          merged.invoicedDays ?? null,
          customerId,
          damagedCarId,
          rentalCarId,
          merged.status,
          '',
          merged.caseNumber || null,
          merged.signatureContentId || (merged.customData?.signatureContentId as string) || null,
          merged.startCarKm ?? null,
          merged.endCarKm ?? null,
          merged.startFuelLevel || 'FULL',
          merged.endFuelLevel || 'FULL',
          merged.rentalCarReturnDate ?? null,
          merged.customData as unknown as Record<string, unknown>
        )

        return await this.get(api, id)
      } catch (e) {
        console.error('[caseStore] Failed to update case:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    async setProcessStatus(
      api: API,
      caseId: string,
      newStatus: CaseProcessStatus,
      extraCustomData?: Record<string, unknown>
    ): Promise<RentalCase | null> {
      const existing = this.cases[caseId]
      if (!existing) {
        console.warn('[caseStore] setProcessStatus: case not found:', caseId)
        return null
      }

      const oldStatus = resolveProcessStatus(existing)
      if (oldStatus === newStatus && !extraCustomData) return existing

      const customData = {
        ...existing.customData,
        ...extraCustomData,
        processStatus: newStatus
      }

      const isClosing = newStatus === 'ENDED' && existing.status !== 'ENDED'
      const fresh = isClosing
        ? await this.closeRentalCase(api, caseId, {
            ...customData,
            endCarKm: existing.endCarKm || 0,
            endFuelLevel: existing.endFuelLevel || 'FULL'
          })
        : await this.update(api, caseId, { customData })

      if (fresh && oldStatus !== newStatus) {
        await this.createAudit(api, caseId, 'STATUS_CHANGED', { oldStatus, newStatus })
      }

      if (newStatus === 'ENDED' && oldStatus !== 'ENDED' && fresh) {
        try {
          await api.notifications.createNotificationEvent('RENTAL_CASE_ENDED', null, null
          )
        } catch (notifError) {
          console.error('[caseStore] RENTAL_CASE_ENDED notification failed:', notifError)
        }
      }

      return fresh
    },

    async save(api: API, data: RentalCase): Promise<RentalCase | null> {
      const newPs = resolveProcessStatus(data)
      const existing = this.cases[data.id]
      const oldPs = existing ? resolveProcessStatus(existing) : undefined

      if (newPs === 'ENDED' && oldPs !== 'ENDED') {
        return this.setProcessStatus(api, data.id, 'ENDED', data.customData as Record<string, unknown>)
      }

      const fresh = await this.update(api, data.id, data)
      if (fresh && oldPs && oldPs !== newPs) {
        await this.createAudit(api, data.id, 'STATUS_CHANGED', { oldStatus: oldPs, newStatus: newPs })
      }
      return fresh
    },

    // Thin wrapper around the dedicated closeRentalCase BE endpoint, which
    // replaces (not merges) customData — caller must pass the full object.
    async closeRentalCase(
      api: API,
      caseId: string,
      customData: Record<string, unknown>
    ): Promise<RentalCase | null> {
      if (!api?.rentalCase) {
        throw new Error('RentalCase API not available')
      }
      try {
        await api.rentalCase.closeRentalCase(caseId, customData)
        return await this.get(api, caseId)
      } catch (e) {
        console.error('[caseStore] Failed to close case:', e)
        throw e
      }
    },

    async fetchAudits(api: API, caseId: string): Promise<RentalAudit[]> {
      if (!api?.rentalCase) {
        console.warn('[caseStore] api.rentalCase not available')
        return []
      }

      try {
        const response = await api.rentalCase.getAllRentalAuditsByRentalCaseId(caseId)
        const audits = (Array.isArray(response) ? response : []) as RentalAudit[]

        this.audits[caseId] = audits
        return audits
      } catch (e) {
        console.error('[caseStore] Failed to fetch audits:', e)
        return []
      }
    },

    async createAudit(
      api: API,
      caseId: string,
      action: string,
      customData: RentalAuditCustomData
    ): Promise<RentalAudit | null> {
      if (!api?.rentalCase) {
        console.warn('[caseStore] api.rentalCase not available')
        return null
      }

      try {
        const response = await api.rentalCase.createRentalAudit(caseId, action, customData)
        const audit = response as unknown as RentalAudit

        // Fetch fresh audits from BE to ensure state consistency
        await this.fetchAudits(api, caseId)

        return audit
      } catch (e) {
        console.error('[caseStore] Failed to create audit:', e)
        return null
      }
    }
  }
})

// Re-export types consumed via ~/stores barrel
export type { RentalCase } from '~/api/types/rentalCase'
